import Icon from "@material-ui/icons/LocalShipping";
import { FC } from "react";
import {
  AutocompleteInput,
  Create,
  CreateProps,
  Datagrid,
  DateTimeInput,
  Filter,
  List,
  ListProps,
  NumberInput,
  Record,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  Show,
  ShowProps,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import CrudBreadcrumb from "../../components/CrudBreadcrumb";
import DeliveryInfo from "../../components/DeliveryInfo";
import {
  AllocationIdField,
  BookingIdField,
  DeliveryIdField,
  LocalDateField,
  ProductInstanceIdField,
} from "../../components/fields";
import DeliveryLocationField from "../../components/fields/DeliveryLocationField";
import PickupInfo from "../../components/PickupInfo";
import VerificationInfo from "../../components/VerificationInfo";
import { foundIn } from "../../utils";
import { convertJsToSql } from "../../utils/date";
import DeliveryBulkActions from "./actions/DeliveryBulkActions";
import DeliveryActions from "./DeliveryActions";
import { deliveryStatus } from "./util";

const ResourceList: FC<ListProps> = (props) => {
  // const [isAssigning, setIsAssigning] = useState<boolean>(true);
  // const [selectedDriver, setSelectedDriver] = useState<number>(-1);

  const isRowSelectable = (record: Record): boolean => {
    // Don't allow selection for verified deliveries (delivered + verified)
    if (record.status === deliveryStatus.DELIVERED && record.verify_at) {
      return false;
    }

    // Don't allow selection for cannot pick deliveries
    if (record.status === deliveryStatus.CANNOT_PICK) {
      return false;
    }

    // Allow selection for deliveries that can be assigned, started, finished, or verified
    return foundIn(record.status, [
      deliveryStatus.NEW,
      deliveryStatus.ASSIGNED,
      deliveryStatus.PICKED,
      deliveryStatus.DELIVERED,
    ]);
  };

  // Transform filter to handle nested business unit filtering and delivery status
  const transformedProps = {
    ...props,
    filter: props.filter
      ? {
          ...props.filter,
          ...(props.filter.business_unit_id && {
            "booking.business_unit_id": props.filter.business_unit_id,
            business_unit_id: undefined,
          }),
          // Handle delivery status filtering for verified vs delivered only
          ...(props.filter.status === "delivery_verified"
            ? {
                status: deliveryStatus.DELIVERED,
                verified: true,
              }
            : props.filter.status === deliveryStatus.DELIVERED
              ? {
                  status: deliveryStatus.DELIVERED,
                  verified: false,
                }
              : {}),
        }
      : {},
  };

  return (
    <List
      {...transformedProps}
      sort={{ field: "booking.is_eot", order: "DESC" }}
      exporter={false}
      filters={<ListFilter />}
      bulkActionButtons={<DeliveryBulkActions />}
      title="Deliveries"
    >
      <Datagrid isRowSelectable={isRowSelectable}>
        <DeliveryIdField />
        <BookingIdField source="booking" />
        <ReferenceField
          label="BU"
          source="booking_id"
          reference="bookings"
          link={false}
        >
          <TextField source="business_unit.name" label="Business Unit" />
        </ReferenceField>
        <ProductInstanceIdField source="product_instance" />
        <TextField
          source="product_instance.location.name"
          label="Current Position"
        />
        <TextField source="target_location.name" label="Target Location" />
        <LocalDateField source="target_delivery_time" showTime />
        <TextField source="driver.full_name" label="Driver" />
        <DeliveryLocationField
          locationIdSource="picked_location_id"
          timeSource="picked_time"
          label="Picked Up At"
        />
        <DeliveryLocationField
          locationIdSource="delivered_location_id"
          timeSource="delivered_time"
          label="Drop Off At"
        />
      </Datagrid>
    </List>
  );
};

const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <TextInput source="id" label="ID" />
    <SelectInput
      source="status"
      choices={[
        { id: deliveryStatus.NEW, name: "New" },
        { id: deliveryStatus.DELIVERED, name: "Delivered" },
        { id: deliveryStatus.ASSIGNED, name: "Assigned" },
        { id: deliveryStatus.PICKED, name: "Picked" },
        { id: deliveryStatus.CANCELLED, name: "Cancelled" },
        { id: deliveryStatus.CANNOT_PICK, name: "Cannot Pick" },
        { id: "delivery_verified", name: "Delivered & Verified" },
      ]}
    />
    <SelectInput
      source="booking.is_eot"
      label={process.env.REACT_APP_COMPANY_CODE === "STMY" ? "UD" : "EOT"}
      choices={[
        { id: 1, name: "true" },
        { id: 0, name: "false" },
      ]}
    />
    <TextInput source="booking_id" />
    <ReferenceInput
      label="Set"
      source="product_instance_id"
      reference="product-instances"
      allowEmpty
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput
      label="Target Location"
      source="target_location_id"
      reference="locations"
      perPage={10000}
    >
      <AutocompleteInput
        optionText="name"
        // shouldRenderSuggestions={(val: string) => {
        //   return val.trim().length > 2;
        // }}
      />
    </ReferenceInput>

    <DateTimeInput
      label="Target Delivery From"
      source="target_delivery_time.from_date"
    />
    <DateTimeInput
      label="Target Delivery To"
      source="target_delivery_time.to_date"
    />

    <ReferenceInput
      label="Driver"
      source="driver_id"
      reference="drivers"
      sort={{ field: "name", order: "ASC" }}
    >
      <AutocompleteInput
        optionText="full_name"
        shouldRenderSuggestions={(val: string) => {
          return val.trim().length > 2;
        }}
      />
    </ReferenceInput>
    <ReferenceInput
      label="Business Unit"
      source="booking.business_unit_id"
      reference="business-units"
      perPage={100}
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

// Wrapper components to use record context
const PickupInfoWrapper = () => {
  const record = useRecordContext();
  return <PickupInfo record={record as any} />;
};

const DeliveryInfoWrapper = () => {
  const record = useRecordContext();
  return <DeliveryInfo record={record as any} />;
};

const VerificationInfoWrapper = () => {
  const record = useRecordContext();
  return <VerificationInfo record={record as any} />;
};

const ResourceShow: FC<ShowProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        mode="show"
        resourceId={props.id}
      />
      <Show {...props} actions={<DeliveryActions />}>
        <SimpleShowLayout>
          <DeliveryIdField />
          <ProductInstanceIdField source="product_instance" />
          <BookingIdField source="booking" />
          <AllocationIdField source="allocation" />
          <ReferenceField
            label="Business Unit"
            source="booking_id"
            reference="bookings"
            link={false}
          >
            <TextField source="business_unit.name" label="Business Unit" />
          </ReferenceField>
          <TextField source="note_to_driver" />
          <TextField
            source="product_instance.location.name"
            label="Current Location"
          />
          <TextField source="target_location.name" label="Target Location" />
          <LocalDateField source="target_delivery_time" showTime />
          <ReferenceField source="driver_id" reference="drivers">
            <TextField source="full_name" />
          </ReferenceField>
          <LocalDateField source="picked_time" showTime />
          <DeliveryLocationField
            locationIdSource="picked_location_id"
            timeSource="picked_time"
            label="Picked Up At"
          />
          <DeliveryLocationField
            locationIdSource="delivered_location_id"
            timeSource="delivered_time"
            label="Drop Off At"
          />

          <PickupInfoWrapper />
          <DeliveryInfoWrapper />
          <VerificationInfoWrapper />
        </SimpleShowLayout>
      </Show>
    </>
  );
};

const ResourceCreate: FC<CreateProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        mode="create"
        resourceId=""
      />
      <Create {...props}>
        <SimpleForm>
          <NumberInput source="booking_id" />
          <NumberInput source="allocation_id" />
          <ReferenceInput
            source="product_instance_id"
            reference="product-instances"
            label="From"
          >
            <AutocompleteInput optionText="location.name" disabled />
          </ReferenceInput>
          <ReferenceInput
            source="target_location_id"
            reference="locations"
            label="To"
            filter={{ is_virtual: false }}
          >
            <SelectInput optionText="name" />
          </ReferenceInput>
          <DateTimeInput source="target_delivery_time" parse={convertJsToSql} />
          <ReferenceInput
            source="product_instance_id"
            reference="product-instances"
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <TextInput multiline source="note_to_driver" label="Note To Driver" />
        </SimpleForm>
      </Create>
    </>
  );
};

const Common = {
  resourceName: "deliveries",
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  name: Common.resourceName,
  icon: Icon,
  create: ResourceCreate,
  list: ResourceList,
  show: ResourceShow,
};
